import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut, Package, RefreshCcw, Settings2, Star, User, Cake, Undo2, Ban } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { CartDrawer } from "@/components/CartDrawer";
import { Footer } from "@/components/Footer";
import { useAuth, formatApiError } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatCents = (c) => `€${(c / 100).toFixed(2).replace(".", ",")}`;

const RETURN_LABELS = {
  requested: { label: "Retour aangevraagd", cls: "bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/30" },
  approved: { label: "Retour goedgekeurd", cls: "bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/30" },
  rejected: { label: "Retour afgewezen", cls: "bg-[#FF087B]/10 text-[#FF087B] border-[#FF087B]/30" },
};

const inputClass =
  "w-full bg-slate-900/70 border border-slate-700 focus:border-[#00FF66] focus:ring-1 focus:ring-[#00FF66]/40 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

const AuthForms = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welkom terug, splatkampioen!");
      } else if (mode === "register") {
        await register(name, email, password);
        toast.success("Account aangemaakt. Welkom bij Gelvaro!");
      } else {
        await axios.post(`${API}/auth/forgot-password`, { email });
        setResetSent(true);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="flex bg-slate-900/70 border border-slate-800 rounded-full p-1 mb-8" data-testid="auth-tabs">
        {[
          ["login", "Inloggen"],
          ["register", "Account aanmaken"],
        ].map(([key, label]) => (
          <button
            key={key}
            data-testid={`auth-tab-${key}`}
            onClick={() => { setMode(key); setError(""); setResetSent(false); }}
            className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all ${
              mode === key ? "bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950" : "text-slate-400 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "forgot" ? (
        <form onSubmit={submit} className="bg-slate-900/60 border border-slate-800 rounded-3xl p-7 space-y-4" data-testid="forgot-form">
          <h2 className="font-display text-2xl uppercase tracking-wide text-white">Wachtwoord vergeten</h2>
          <p className="text-sm text-slate-400">Vul je e-mailadres in — we sturen je een resetlink per e-mail.</p>
          <label className="block">
            <span className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">E-mailadres</span>
            <input data-testid="forgot-email-input" required type="email" className={inputClass}
              placeholder="jan@voorbeeld.nl" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          {error && <p className="text-sm text-[#FF087B] font-semibold" data-testid="forgot-error">{error}</p>}
          {resetSent && (
            <div className="bg-[#00FF66]/10 border border-[#00FF66]/30 rounded-xl p-4" data-testid="forgot-success">
              <p className="text-sm text-slate-200 font-semibold">Check je inbox!</p>
              <p className="text-xs text-slate-400 mt-1">
                Als dit e-mailadres bij ons bekend is, ontvang je binnen een minuut een e-mail met een resetlink. De link is 1 uur geldig.
              </p>
            </div>
          )}
          <button
            type="submit"
            data-testid="forgot-submit-button"
            disabled={busy}
            className="w-full bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold uppercase tracking-wider py-4 rounded-full glow-lime disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Verstuur resetlink
          </button>
          <button type="button" data-testid="forgot-back-login" onClick={() => { setMode("login"); setResetSent(false); }}
            className="w-full text-sm text-slate-400 hover:text-white transition-colors">
            Terug naar inloggen
          </button>
        </form>
      ) : (
        <form onSubmit={submit} className="bg-slate-900/60 border border-slate-800 rounded-3xl p-7 space-y-4" data-testid="auth-form">
          {mode === "register" && (
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Naam</span>
              <input data-testid="register-name-input" required minLength={2} className={inputClass}
                placeholder="Jan de Vries" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
          )}
          <label className="block">
            <span className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">E-mailadres</span>
            <input data-testid="auth-email-input" required type="email" className={inputClass}
              placeholder="jan@voorbeeld.nl" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="block">
            <span className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Wachtwoord</span>
            <input data-testid="auth-password-input" required type="password" minLength={6} className={inputClass}
              placeholder="Minimaal 6 tekens" value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>

          {mode === "login" && (
            <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); }}
              className="text-xs text-slate-400 hover:text-[#00E5FF] transition-colors">
              Wachtwoord vergeten?
            </button>
          )}

          {error && (
            <p className="text-sm text-[#FF087B] font-semibold" data-testid="auth-error">{error}</p>
          )}

          <button
            type="submit"
            data-testid="auth-submit-button"
            disabled={busy}
            className="w-full bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold uppercase tracking-wider py-4 rounded-full glow-lime hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === "login" ? "Inloggen" : "Account aanmaken"}
          </button>
        </form>
      )}
    </div>
  );
};

const ReviewForm = ({ onDone }) => {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/reviews`, { rating, text }, { withCredentials: true });
      toast.success("Bedankt voor je review! Hij staat nu op de homepage.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-slate-900/60 border border-[#00FF66]/30 rounded-3xl p-7" data-testid="review-form">
      <div className="flex items-center gap-3 mb-4">
        <Star className="w-5 h-5 text-[#00FF66] fill-[#00FF66]" />
        <h2 className="font-display text-2xl uppercase tracking-wide text-white">Schrijf een review</h2>
      </div>
      <p className="text-sm text-slate-400 mb-4">Je hebt een bestelling gedaan — hoe beviel je blaster? Je review verschijnt op de homepage.</p>
      <div className="flex gap-1.5 mb-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            type="button"
            data-testid={`review-star-${i}`}
            onClick={() => setRating(i)}
            className="p-1 hover:scale-125 transition-transform"
            aria-label={`${i} sterren`}
          >
            <Star className={`w-7 h-7 ${i <= rating ? "text-[#00FF66] fill-[#00FF66]" : "text-slate-700"}`} />
          </button>
        ))}
      </div>
      <textarea
        data-testid="review-text-input"
        required
        minLength={10}
        maxLength={500}
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Vertel kort je ervaring (min. 10 tekens)..."
        className={`${inputClass} resize-none`}
      />
      <button
        type="submit"
        data-testid="review-submit-button"
        disabled={busy}
        className="mt-4 bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold text-sm uppercase tracking-wider px-6 py-3 rounded-full glow-lime disabled:opacity-60 flex items-center gap-2"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        Review plaatsen
      </button>
    </form>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [canReview, setCanReview] = useState({ has_paid_order: false, has_review: false });
  const [loading, setLoading] = useState(true);
  const [portalBusy, setPortalBusy] = useState(false);
  const [birthday, setBirthday] = useState(user.birthday || "");
  const [birthdayBusy, setBirthdayBusy] = useState(false);
  const [returnOpen, setReturnOpen] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [returnBusy, setReturnBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/account/orders`, { withCredentials: true }).catch(() => ({ data: { orders: [] } })),
      axios.get(`${API}/account/subscription`, { withCredentials: true }).catch(() => ({ data: { subscription: null } })),
      axios.get(`${API}/account/can-review`, { withCredentials: true }).catch(() => ({ data: { has_paid_order: false, has_review: false } })),
    ]).then(([o, s, r]) => {
      setOrders(o.data.orders || []);
      setSubscription(s.data.subscription);
      setCanReview(r.data);
      setLoading(false);
    });
  }, []);

  const manageSubscription = async () => {
    setPortalBusy(true);
    try {
      const res = await axios.post(
        `${API}/account/portal`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = res.data.portal_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Kon de beheerportal niet openen"));
      setPortalBusy(false);
    }
  };

  const submitReturn = async (orderId) => {
    setReturnBusy(true);
    try {
      await axios.post(`${API}/orders/${orderId}/return`, { reason: returnReason.trim() }, { withCredentials: true });
      toast.success("Retouraanvraag verstuurd — we beoordelen hem zo snel mogelijk.");
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? { ...o, return_status: "requested", return_reason: returnReason.trim() } : o)));
      setReturnOpen(null);
      setReturnReason("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setReturnBusy(false);
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm("Weet je zeker dat je deze bestelling wilt annuleren? Dit kan niet ongedaan worden gemaakt.")) return;
    setCancelBusy(orderId);
    try {
      await axios.post(`${API}/orders/${orderId}/cancel`, {}, { withCredentials: true });
      toast.success("Bestelling geannuleerd — je terugbetaling volgt binnen 14 dagen.");
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? { ...o, fulfillment_status: "geannuleerd" } : o)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCancelBusy(null);
    }
  };

  const saveBirthday = async () => {
    setBirthdayBusy(true);
    try {
      await axios.patch(`${API}/account/profile`, { birthday }, { withCredentials: true });
      toast.success("Verjaardag opgeslagen — de kortingsmail komt eraan op je grote dag!");
      user.birthday = birthday;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBirthdayBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="account-dashboard">
      <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-7 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00FF66] to-[#00E5FF] flex items-center justify-center">
            <User className="w-7 h-7 text-slate-950" />
          </div>
          <div>
            <p className="font-bold text-white text-lg" data-testid="account-name">{user.name}</p>
            <p className="text-sm text-slate-400" data-testid="account-email">{user.email}</p>
          </div>
        </div>
        <button
          data-testid="logout-button"
          onClick={async () => { await logout(); toast.success("Uitgelogd. Tot de volgende splat!"); }}
          className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors"
        >
          <LogOut className="w-4 h-4" /> Uitloggen
        </button>
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-7" data-testid="account-subscription-card">
        <div className="flex items-center gap-3 mb-4">
          <RefreshCcw className="w-5 h-5 text-[#00E5FF]" />
          <h2 className="font-display text-2xl uppercase tracking-wide text-white">Mijn abonnement</h2>
        </div>
        {loading ? (
          <p className="text-sm text-slate-500">Laden...</p>
        ) : subscription ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-bold text-[#00FF66]" data-testid="subscription-name">{subscription.plan_name}</p>
                <p className="text-xs text-slate-500 mt-1">Actief — elke maand op de mat. Beheer, pauzeer of zeg op via de beveiligde portal.</p>
              </div>
              <span className="bg-[#00FF66]/10 text-[#00FF66] border border-[#00FF66]/30 px-3 py-1 rounded-full text-xs font-mono-brand uppercase tracking-widest" data-testid="subscription-status">
                Actief
              </span>
            </div>
            <button
              data-testid="manage-subscription-button"
              onClick={manageSubscription}
              disabled={portalBusy}
              className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-[#00E5FF]/50 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors disabled:opacity-60"
            >
              {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings2 className="w-4 h-4" />}
              Abonnement beheren
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-slate-400" data-testid="subscription-none">Nog geen abonnement. Elke maand verse orbs of een mystery pack op de mat?</p>
            <button
              data-testid="subscription-cta-button"
              onClick={() => navigate("/abonnementen")}
              className="bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold text-sm uppercase px-5 py-2.5 rounded-full"
            >
              Bekijk abonnementen
            </button>
          </div>
        )}
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-7" data-testid="account-birthday-card">
        <div className="flex items-center gap-3 mb-4">
          <Cake className="w-5 h-5 text-[#00FF66]" />
          <h2 className="font-display text-2xl uppercase tracking-wide text-white">Je verjaardag</h2>
        </div>
        <p className="text-sm text-slate-400 mb-4">
          Vul je geboortedatum in en krijg op je verjaardag een feestelijke e-mail met een persoonlijke kortingscode van 15%.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="date"
            data-testid="birthday-input"
            value={birthday}
            onChange={(e) => setBirthday(e.target.value)}
            className="bg-slate-900/70 border border-slate-700 focus:border-[#00FF66] rounded-xl px-4 py-2.5 text-sm text-white outline-none [color-scheme:dark]"
          />
          <button
            data-testid="birthday-save-button"
            onClick={saveBirthday}
            disabled={birthdayBusy || !birthday}
            className="bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold text-sm uppercase px-5 py-2.5 rounded-full disabled:opacity-60 flex items-center gap-2"
          >
            {birthdayBusy && <Loader2 className="w-4 h-4 animate-spin" />}
            Opslaan
          </button>
          {user.birthday && (
            <span className="text-xs text-slate-500" data-testid="birthday-saved">
              Opgeslagen: {new Date(user.birthday + "T00:00:00").toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}
            </span>
          )}
        </div>
      </div>

      {!loading && canReview.has_paid_order && !canReview.has_review && (
        <ReviewForm onDone={() => setCanReview((c) => ({ ...c, has_review: true }))} />
      )}

      <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-7" data-testid="account-orders-card">
        <div className="flex items-center gap-3 mb-4">
          <Package className="w-5 h-5 text-[#FF087B]" />
          <h2 className="font-display text-2xl uppercase tracking-wide text-white">Mijn bestellingen</h2>
        </div>
        {loading ? (
          <p className="text-sm text-slate-500">Laden...</p>
        ) : orders.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="orders-empty">Nog geen bestellingen. Tijd voor je eerste blaster.</p>
        ) : (
          <ul className="space-y-3">
            {orders.map((o) => (
              <li key={o.order_id} className="bg-slate-800/40 rounded-2xl px-4 py-3" data-testid={`order-row-${o.order_id}`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-white font-mono-brand">{o.order_id}</p>
                    <p className="text-xs text-slate-500">
                      {o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-[#00E5FF]">{formatCents(o.amount_total)}</p>
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider font-mono-brand" data-testid={`order-fulfillment-${o.order_id}`}>
                      {o.fulfillment_status || "nieuw"}
                    </p>
                  </div>
                </div>
                {o.tracking_code && (o.fulfillment_status === "verzonden" || o.fulfillment_status === "geleverd") && (
                  <p className="text-xs text-[#00E5FF] mt-2" data-testid={`order-tracking-${o.order_id}`}>
                    Track & trace:{" "}
                    {o.tracking_code.startsWith("http") ? (
                      <a href={o.tracking_code} target="_blank" rel="noopener noreferrer" className="underline font-bold">
                        Volg je pakket
                      </a>
                    ) : (
                      <span className="font-bold">{o.tracking_code}</span>
                    )}
                  </p>
                )}
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap border-t border-slate-700/40 pt-3">
                  {o.fulfillment_status === "geannuleerd" ? (
                    <span
                      data-testid={`order-canceled-badge-${o.order_id}`}
                      className="px-2.5 py-0.5 rounded-full text-[11px] font-mono-brand uppercase tracking-wider border bg-[#FF087B]/10 text-[#FF087B] border-[#FF087B]/30"
                    >
                      Geannuleerd
                    </span>
                  ) : (
                    <>
                      {o.return_status && RETURN_LABELS[o.return_status] ? (
                        <span
                          data-testid={`order-return-status-${o.order_id}`}
                          className={`px-2.5 py-0.5 rounded-full text-[11px] font-mono-brand uppercase tracking-wider border ${RETURN_LABELS[o.return_status].cls}`}
                        >
                          {RETURN_LABELS[o.return_status].label}
                        </span>
                      ) : (
                        <span className="text-[11px] text-slate-600 uppercase tracking-wider font-mono-brand">Geen retour</span>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        {(o.fulfillment_status || "nieuw") === "nieuw" && !["requested", "approved"].includes(o.return_status) && (
                          <button
                            data-testid={`order-cancel-button-${o.order_id}`}
                            onClick={() => cancelOrder(o.order_id)}
                            disabled={cancelBusy === o.order_id}
                            className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-[#FF087B]/15 border border-slate-700 hover:border-[#FF087B]/50 text-slate-200 text-xs font-bold px-3 py-1.5 rounded-full transition-colors disabled:opacity-50"
                          >
                            {cancelBusy === o.order_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />} Annuleren
                          </button>
                        )}
                        {!o.return_status && returnOpen !== o.order_id && (
                          <button
                            data-testid={`order-return-button-${o.order_id}`}
                            onClick={() => { setReturnOpen(o.order_id); setReturnReason(""); }}
                            className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-[#FF087B]/15 border border-slate-700 hover:border-[#FF087B]/50 text-slate-200 text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                          >
                            <Undo2 className="w-3.5 h-3.5" /> Retour aanvragen
                          </button>
                        )}
                        {o.return_status === "rejected" && returnOpen !== o.order_id && (
                          <button
                            data-testid={`order-return-retry-${o.order_id}`}
                            onClick={() => { setReturnOpen(o.order_id); setReturnReason(""); }}
                            className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-[#FF087B]/15 border border-slate-700 hover:border-[#FF087B]/50 text-slate-200 text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                          >
                            <Undo2 className="w-3.5 h-3.5" /> Opnieuw aanvragen
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {returnOpen === o.order_id && (
                  <div className="mt-3 bg-slate-900/70 border border-slate-700 rounded-2xl p-4" data-testid={`order-return-form-${o.order_id}`}>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Reden voor retour / annulering</p>
                    <textarea
                      data-testid={`order-return-reason-${o.order_id}`}
                      rows={2}
                      minLength={5}
                      maxLength={600}
                      value={returnReason}
                      onChange={(e) => setReturnReason(e.target.value)}
                      placeholder="Vertel kort waarom je wilt retourneren (min. 5 tekens)..."
                      className={`${inputClass} resize-none`}
                    />
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        data-testid={`order-return-submit-${o.order_id}`}
                        onClick={() => submitReturn(o.order_id)}
                        disabled={returnBusy || returnReason.trim().length < 5}
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold text-xs uppercase px-4 py-2 rounded-full disabled:opacity-50"
                      >
                        {returnBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        Verstuur aanvraag
                      </button>
                      <button
                        data-testid={`order-return-cancel-${o.order_id}`}
                        onClick={() => { setReturnOpen(null); setReturnReason(""); }}
                        className="text-xs text-slate-400 hover:text-white font-bold px-3 py-2 transition-colors"
                      >
                        Annuleren
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default function Account() {
  const { user } = useAuth();

  return (
    <div data-testid="account-page" className="bg-[#090D16] text-slate-100 min-h-screen">
      <div className="noise-overlay" />
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-10 text-center">
          <h1 className="font-display text-6xl sm:text-7xl uppercase tracking-wide text-white leading-none">
            {user ? <>Hoi, <span className="text-gradient-splat">{user.name?.split(" ")[0]}</span></> : <>Jouw <span className="text-gradient-splat">account</span></>}
          </h1>
          <p className="mt-4 text-slate-400">
            {user ? "Beheer je abonnement en bekijk je bestellingen." : "Log in of maak een account om je bestellingen en abonnement te beheren."}
          </p>
        </motion.div>
        {user === null ? (
          <p className="text-center text-slate-500" data-testid="account-loading">Laden...</p>
        ) : user ? (
          <Dashboard />
        ) : (
          <AuthForms />
        )}
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
